import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterButton } from './filter-button';
import { FilterChip } from './filter-chip';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

interface FilterSystemProps<T extends string> {
  type: string;
  options: FilterOption<T>[];
  selected: T[];
  onChange: (values: T[]) => void;
  className?: string;
}

export function FilterSystem<T extends string>({
  type,
  options,
  selected,
  onChange,
  className
}: FilterSystemProps<T>) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-1.5">
            <FilterButton
              label={type}
              isActive={open}
              selectedCount={selected.length}
              onClick={() => {
                setOpen(true);
              }}
            />
            <motion.div
              layout
              transition={{
                type: 'spring',
                bounce: 0,
                duration: 0.3
              }}
              className="flex items-center gap-1.5"
            >
              <AnimatePresence initial={false}>
                {selected.map((value) => {
                  const option = options.find((o) => o.value === value);
                  if (!option) return null;

                  return (
                    <FilterChip
                      key={value}
                      label={option.label}
                      onRemove={() => {
                        onChange(selected.filter((item) => item !== value));
                      }}
                    />
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-0" align="start" sideOffset={8}>
          <Command>
            <CommandInput placeholder={`Search ${type.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No {type.toLowerCase()} found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    className="cursor-pointer"
                    key={option.value}
                    onSelect={() => {
                      onChange(
                        selected.includes(option.value)
                          ? selected.filter((item) => item !== option.value)
                          : [...selected, option.value]
                      );
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {option.icon}
                      <span>{option.label}</span>
                    </div>
                    {selected.includes(option.value) && <Check className="h-4 w-4 ml-2" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
